import { API_BASE_URL } from "../../config";

let csrfToken: string | null = null;

const fetchCsrfToken = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/csrf-token`, { credentials: "include" });
    if (response.ok) {
      const data = await response.json();
      csrfToken = data.csrfToken;
    }
  } catch (error) {
    console.error("Error fetching CSRF token:", error);
  }
};

const apiFetch = async (endpoint: string, options: RequestInit = {}, isPublic: boolean = false) => {
  if (!csrfToken) {
    await fetchCsrfToken();
  }

  const headers: any = {
    "Content-Type": "application/json",
    "X-Client-Type": "Web", // Added for server-side client type identification
    ...options.headers,
  };

  if (csrfToken) {
    headers["X-CSRF-Token"] = csrfToken;
  }

  if (!isPublic) {
    const token = localStorage.getItem("token");
    if (token) {
      headers.Authorization = `Bearer ${JSON.parse(token)}`;
    } else {
      // Handle case where token is missing for a protected route
      window.location.href = "/login";
      return;
    }
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: "include",
    });

    if (response.status === 401 || response.status === 403) {
      // Clear local storage and redirect to login
      localStorage.removeItem("user");
      localStorage.removeItem("userId");
      localStorage.removeItem("userRole");
      localStorage.removeItem("headquarterId");
      localStorage.removeItem("headsetId");
      localStorage.removeItem("firstName");
      localStorage.removeItem("lastName");
      localStorage.removeItem("token");
      window.location.href = "/login";
      return;
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: "Failed to fetch" }));
      throw new Error(errorData.error || `Request failed with status ${response.status}`);
    }

    return await response.json();

  } catch (error) {
    console.error(`Error fetching ${endpoint}:`, error);
    throw error;
  }
};

export const checkForAdmin = (userId: string) => apiFetch("/checkForAdmin", { method: "POST", body: JSON.stringify({ userId }) });

export const getCountries = () => apiFetch("/getCountries", { method: "POST" });

export const getCountryDataByHeadsetId = (headsetId) => apiFetch("/getCountryDataByGroupId", { method: "POST", body: JSON.stringify({ groupId: headsetId }) });

export const getHeadquarterNameById = (headquarterId) => apiFetch("/getHeadquarterNameById", { method: "POST", body: JSON.stringify({ headquarterId }) });

export const getHeadquarterNameByHeadsetId = (headsetId) => apiFetch("/getHeadquarterNameByHeadsetId", { method: "POST", body: JSON.stringify({ headsetId }) });

export const getHeadsetNameById = (headsetId) => apiFetch("/getGroupNameById", { method: "POST", body: JSON.stringify({ groupId: headsetId }) });

export const getHeadsets = (headquarterId?: string) => apiFetch("/getHeadsets", { method: "POST", body: JSON.stringify({ headquarterId }) });

export const getHeadquarters = () => apiFetch("/getHeadquarters", { method: "POST" });

export const getHeadquartersWithLicenses = (headquarterId?: string) => apiFetch("/getHeadquartersWithLicenses", { method: "POST", body: JSON.stringify({ headquarterId }) });



export const getHospitalsInHeadquarter = (headquarterId) => apiFetch("/getHospitalsInHeadquarter", { method: "POST", body: JSON.stringify({ headquarterId }) });

export const getAllHospitals = () => apiFetch("/getAllHospitals", { method: "POST" });

export const getUserDataById = (userId) => apiFetch("/getUserDataById", { method: "POST", body: JSON.stringify({ userId }) });

export const getTrainingsListWithPermissions = (entityId, entityType, showAll) => apiFetch("/getTrainingsListWithPermissions", { method: "POST", body: JSON.stringify({ entityId, entityType, showAll }) });

export const getManufacturersData = () => apiFetch("/getManufacturersData", { method: "POST" });

export const getComingSoonManufacturers = (entityId, entityType, showAll) => apiFetch("/getComingSoonManufacturers", { method: "POST", body: JSON.stringify({ entityId, entityType, showAll }) });

export const updateTrainingPermission = (entityId, entityType, trainingId, isChecked, isHidden) => apiFetch("/updateTrainingPermission", { method: "POST", body: JSON.stringify({ entityId, entityType, trainingId, isChecked, isHidden }) });

export const getTrainingsHistory = (userId) => apiFetch("/getTrainingsHistory", { method: "POST", body: JSON.stringify({ userId }) });

export const getLicensesForHq = (headquarterId) => apiFetch("/getLicensesForHq", { method: "POST", body: JSON.stringify({ headquarterId }) });

export const getAllLicenses = () => apiFetch("/getLicenses", { method: "POST" });

export const getLicensePermissions = (licenseId: string) => apiFetch(`/api/licenses/${licenseId}/permissions`, { method: "GET" });

export const updateLicensePermissionsBulk = async (
  licenseId: string,
  updates: { trainingId: string; isChecked: boolean; isHidden: boolean }[]
) => {
  const endpoint = `/api/licenses/${licenseId}/permissions/bulk`;
  return await apiFetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ updates }),
  });
};

export const getLicensesForHeadset = (headsetId: string) => apiFetch(`/getLicensesForHeadset`, { method: "POST", body: JSON.stringify({ headsetId }) });

export const getDevicesForHeadset = (headsetId: string) => apiFetch(`/api/devices/headset/${headsetId}`, { method: "GET" });

export const pairHeadset = (pairingCode: string, headquarterId: string, userId: string, licenseId?: string, name?: string, adminIds?: string[]) => apiFetch("/api/headsets/pair", { method: "POST", body: JSON.stringify({ pairingCode, headquarterId, userId, licenseId, name, adminIds }) });

export const remoteStart = (deviceId: string, trainingId: string, userId?: string, logoutAfterTraining?: boolean, manufacturerId?: string, sceneName?: string, scenarioName?: string) => apiFetch("/remote-start", { method: "POST", body: JSON.stringify({ deviceId, trainingId, userId, logoutAfterTraining, manufacturerId, sceneName, scenarioName }) });

export const deleteHeadset = (headsetId: string) => apiFetch(`/api/headsets/${headsetId}/unpair`, { method: "DELETE" });

export const getHeadsetsForAdmin = (userId: string) => apiFetch("/getHeadsetsForAdmin", { method: "POST", body: JSON.stringify({ userId }) });

export const getUsersInGroup = (groupId) => apiFetch("/getUsersInGroup", { method: "POST", body: JSON.stringify({ groupId }) });

export const getUsersInHeadquarter = (headquarterId) => apiFetch("/getUsersInHeadquarter", { method: "POST", body: JSON.stringify({ headquarterId }) });

export const updateHeadset = (headsetId: string, name: string, adminIds: string[]) => apiFetch(`/api/headsets/${headsetId}/update`, { method: "POST", body: JSON.stringify({ name, adminIds }) });

export const getTrainingPlans = (filters: { headquarterId?: string; deviceId?: string; licenseId?: string } = {}) => {
    const params = new URLSearchParams(filters as any).toString();
    return apiFetch(`/getTrainingPlans?${params}`, { method: "GET" });
};

export const createTrainingPlan = (planData: any) => apiFetch("/createTrainingPlan", { method: "POST", body: JSON.stringify(planData) });

export const createMultipleTrainingPlans = (plansData: any[]) => apiFetch("/createMultipleTrainingPlans", { method: "POST", body: JSON.stringify(plansData) });

export const updateTrainingPlan = (planId: string, updateData: any) => apiFetch(`/api/trainingPlans/${planId}/update`, { method: "POST", body: JSON.stringify(updateData) });

export const getTrainingPlanPermissions = (planId: string) => apiFetch(`/api/trainingPlans/${planId}/permissions`, { method: "GET" });

export const updateTrainingPlanPermission = (planId: string, trainingId: string, isChecked: boolean, isHidden: boolean) => apiFetch(`/api/trainingPlans/${planId}/permissions`, { method: "POST", body: JSON.stringify({ trainingId, isChecked, isHidden }) });

export const deleteTrainingPlan = (planId: string) => apiFetch("/deleteTrainingPlan", { method: "POST", body: JSON.stringify({ planId }) });

export const getUnpairedHeadsets = (headquarterId?: string) => apiFetch("/api/headsets/unpaired", { method: "POST", body: JSON.stringify({ headquarterId }) });

export const assignLicenseToHeadset = (headsetId: string, licenseId: string, adminIds: string[]) => apiFetch("/api/headsets/assignLicense", { method: "POST", body: JSON.stringify({ headsetId, licenseId, adminIds }) });

export const unpairHeadset = (licenseId: string) => apiFetch(`/api/licenses/${licenseId}/unpair`, { method: "POST" });

// Note: signIn, signUp, and resendEmailVerification will be handled in auth.tsx
// We need to export apiFetch so it can be used there.
export default apiFetch;
